/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Keep GemPages sticky add-to-cart forms aligned with the active product variant.
 *
 * The sticky bar renders a separate product form. The theme updates the main
 * product form on `variant:changed`, but GemPages keeps its initial variant ID
 * unless it is updated explicitly.
 */
(() => {
  if (window.SKStickyVariantSync) {
    return;
  }

  window.SKStickyVariantSync = {
    currentVariantId: null,

    getMainVariantId() {
      const mainVariantInput = Array.from(document.querySelectorAll('product-form input[name="id"]')).find(
        (input) => !input.closest('gp-sticky') && !input.disabled
      );

      return mainVariantInput?.value || new URL(window.location.href).searchParams.get('variant');
    },

    sync(variantId = null) {
      const nextVariantId = String(variantId || this.currentVariantId || this.getMainVariantId() || '');

      if (!nextVariantId) {
        return;
      }

      this.currentVariantId = nextVariantId;

      document.querySelectorAll('gp-sticky product-form input[name="id"]').forEach((input) => {
        if (input.value === nextVariantId) {
          return;
        }

        input.value = nextVariantId;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },
  };

  document.addEventListener('variant:changed', (event) => {
    window.SKStickyVariantSync.sync(event.detail?.variant?.id);
  });

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('gp-sticky [name="add"], gp-sticky button[type="submit"]')) {
        window.SKStickyVariantSync.sync();
      }
    },
    true
  );
})();

/**
 * Filter product color swatches by shade group.
 *
 * Quick-buy drawers and popovers are fetched and inserted after page load.
 * Keeping this behavior in the globally loaded custom.js makes it available
 * to both the PDP and dynamically rendered quick-buy content.
 */
(() => {
  if (window.SKShadeTabs) {
    return;
  }

  window.SKShadeTabs = {
    init(root = document) {
      if (!root.querySelectorAll) {
        return;
      }

      root.querySelectorAll('[data-shade-tabs]').forEach((tabs) => {
        if (tabs.dataset.shadeTabsInitialized === 'true') {
          return;
        }

        tabs.dataset.shadeTabsInitialized = 'true';
        this.apply(tabs, 'all', false);
      });
    },

    apply(tabs, group, shouldChangeSelection) {
      const selector = tabs.closest('.product-form__option-selector');
      const list = selector ? selector.querySelector('[data-shade-swatch-list]') : null;

      if (!list) {
        return;
      }

      tabs.querySelectorAll('[data-shade-tab]').forEach((tab) => {
        const isActive = tab.dataset.shadeTab === group;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (shouldChangeSelection) {
        this.centerActiveTab(tabs, group);
      }

      list.querySelectorAll('.color-swatch').forEach((swatch) => {
        const isVisible = group === 'all' || swatch.dataset.shadeGroup === group;
        swatch.classList.toggle('sk-shade-group-hidden', !isVisible);
      });

      if (!shouldChangeSelection || group === 'all') {
        return;
      }

      const checkedInput = list.querySelector('.color-swatch__radio:checked');
      const checkedSwatch = checkedInput ? checkedInput.closest('.color-swatch') : null;

      if (checkedSwatch && !checkedSwatch.classList.contains('sk-shade-group-hidden')) {
        return;
      }

      const nextInput = Array.from(list.querySelectorAll('.color-swatch__radio')).find((input) => {
        const swatch = input.closest('.color-swatch');

        return (
          swatch &&
          !input.disabled &&
          !swatch.hidden &&
          !swatch.classList.contains('is-disabled') &&
          !swatch.classList.contains('sk-shade-group-hidden')
        );
      });

      if (nextInput && !nextInput.checked) {
        nextInput.checked = true;
        nextInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    centerActiveTab(tabs, group) {
      const escapedGroup = window.CSS && CSS.escape ? CSS.escape(group) : group.replace(/"/g, '\\"');
      const activeTab = tabs.querySelector(`[data-shade-tab="${escapedGroup}"]`);

      if (!activeTab) {
        return;
      }

      const targetLeft = activeTab.offsetLeft - (tabs.clientWidth - activeTab.offsetWidth) / 2;

      tabs.scrollTo({
        left: Math.max(targetLeft, 0),
        behavior: 'smooth',
      });
    },
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-shade-tab]');

    if (!tab) {
      return;
    }

    const tabs = tab.closest('[data-shade-tabs]');

    if (!tabs) {
      return;
    }

    window.SKShadeTabs.apply(tabs, tab.dataset.shadeTab, false);
    window.SKShadeTabs.centerActiveTab(tabs, tab.dataset.shadeTab);
  });

  document.addEventListener('shopify:section:load', (event) => {
    window.SKShadeTabs.init(event.target);
  });

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        if (node.matches('[data-shade-tabs]')) {
          window.SKShadeTabs.init(node.parentElement || document);
        } else if (node.querySelector('[data-shade-tabs]')) {
          window.SKShadeTabs.init(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.SKShadeTabs.init(document);
})();
